import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import api from '../lib/api';
import { QRCodeCanvas } from 'qrcode.react';
import { Wallet, Loader2, Gift } from 'lucide-react';

const fmtEur = (n) => `\u20ac ${Number(n || 0).toFixed(2)}`;

/**
 * Pagina pubblica del buono regalo (accessibile scansionando il QR, senza
 * login). Mostra logo attivita', destinatario, saldo reale e ultimi movimenti.
 */
export default function PublicGiftCard() {
  const { t } = useTranslation();
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/public/giftcard/${code}`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setError(e?.response?.status === 404 ? t('publicGift.notFound') : t('publicGift.loadError'));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [code, t]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <Gift className="h-12 w-12 text-slate-300 mb-3" />
        <div className="text-lg font-semibold text-slate-700">{error}</div>
        <div className="text-sm text-slate-500 mt-1">{t('publicGift.code')}: {code}</div>
      </div>
    );
  }

  const b = data.business || {};
  const c = data.card || {};
  const disabled = c.status === 'disabled';
  const depleted = c.status === 'depleted';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 py-8 px-4 flex justify-center">
      <div className="w-full max-w-md">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-br from-emerald-600 to-teal-600 px-6 py-7 text-center text-white">
            {b.logo ? (
              <img src={b.logo} alt={b.name} className="mx-auto max-h-20 max-w-[70%] object-contain mb-2 bg-white/90 rounded-xl p-2" />
            ) : (
              <div className="text-2xl font-extrabold tracking-tight">{b.name}</div>
            )}
            {b.logo && b.name ? <div className="text-sm font-semibold opacity-95">{b.name}</div> : null}
            <div className="mt-1 text-xs uppercase tracking-widest opacity-90">{t('publicGift.giftCard')}</div>
          </div>

          <div className="px-6 py-6">
            {c.recipient_name ? (
              <div className="text-center">
                <div className="text-sm text-slate-500">{t('publicGift.voucherFor')}</div>
                <div className="text-xl font-bold text-slate-800">{c.recipient_name}</div>
              </div>
            ) : null}
            {c.message ? <div className="mt-2 text-center text-sm italic text-slate-600">“{c.message}”</div> : null}

            <div className="mt-5 rounded-2xl bg-emerald-50 border border-emerald-100 py-5 text-center">
              <div className="flex items-center justify-center gap-2 text-emerald-600">
                <Wallet className="h-5 w-5" />
                <span className="text-5xl font-extrabold leading-none text-slate-800">{fmtEur(c.balance)}</span>
              </div>
              <div className="text-sm font-medium text-slate-500 mt-1">{t('publicGift.availableBalance')}</div>
            </div>

            {disabled ? (
              <div className="mt-4 text-center text-sm font-semibold text-rose-600">{t('publicGift.disabled')}</div>
            ) : depleted ? (
              <div className="mt-4 text-center text-sm font-semibold text-slate-500">{t('publicGift.depleted')}</div>
            ) : null}

            {c.history && c.history.length > 0 && (
              <div className="mt-5">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{t('publicGift.lastMovements')}</div>
                <div className="space-y-1.5">
                  {c.history.map((h, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5">
                      <span className="text-slate-500 truncate mr-2">{h.note || h.type}</span>
                      <span className={`font-semibold shrink-0 ${h.amount >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>{h.amount >= 0 ? '+' : ''}{fmtEur(h.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-col items-center">
              <div className="bg-white p-2 rounded-xl border border-slate-200">
                <QRCodeCanvas value={typeof window !== 'undefined' ? window.location.href : ''} size={120} includeMargin />
              </div>
              <div className="mt-2 font-mono text-sm tracking-widest text-slate-500">{c.code}</div>
            </div>
          </div>
        </div>
        <div className="text-center text-xs text-slate-400 mt-4">{t('publicGift.footer')}</div>
      </div>
    </div>
  );
}
